import { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Heading,
    Card,
    CardBody,
    Flex,
    Badge,
    Center,
    Spinner,
    useToast,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    useDisclosure,
    HStack,
    Text,
    Button,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Tooltip,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { MdFilterList, MdDescription, MdOutlineDescription, MdError, MdSearch, MdDelete } from 'react-icons/md';
import { useImportedItems, useMoveToPipeline, useAgencies, useDeleteImportedItem, useBulkDeleteImportedItems, useBulkMoveToPursuits } from '@/services/hooks';
import { DataTable } from '@/components/DataTable/DataTable';
import { ImportedItemsFilters, FilterState } from '@/components/ImportedItems/ImportedItemsFilters';
import { ItemDetailsModal } from '@/components/ImportedItems/ItemDetailsModal';
import { PDFPreviewModal } from '@/components/ImportedItems/PDFPreviewModal';
import { ConfirmDialog } from '@/components/Common/ConfirmDialog';

interface TabContentProps {
    type: 'historical' | 'active' | 'future';
}

function TabContent({ type }: TabContentProps) {
    // State
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<FilterState>({
        agencyIds: [],
        minPrice: undefined,
        maxPrice: undefined,
        dateFrom: undefined,
        dateTo: undefined,
    });
    const [sortBy, setSortBy] = useState<string>(type === 'active' ? 'submission_deadline' : 'created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedItem, setSelectedItem] = useState<any | null>(null);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
    const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();
    const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const toast = useToast();

    // Confirmation Dialog State
    const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
    const [confirmConfig, setConfirmConfig] = useState<{
        title: string;
        message: string;
        confirmText: string;
        confirmColor: string;
        onConfirm: () => void;
        isLoading: boolean;
    } | null>(null);

    const handlePreview = useMemo(() => (url: string) => {
        setPreviewUrl(url);
        onPreviewOpen();
    }, [onPreviewOpen]);

    // Check for running scrapes
    const { data: agenciesData } = useAgencies({ refetchInterval: 5000 });
    const isScraping = agenciesData?.some((a: any) =>
        a.data_feeds.some((f: any) => f.last_run_status === 'running' || f.last_run_status === 'pending')
    );

    // Fetch Data
    const { data, isLoading, refetch } = useImportedItems({
        type,
        status: 'imported',
        page,
        limit: 20,
        search: search || undefined,
        agency_ids: filters.agencyIds.length > 0 ? filters.agencyIds : undefined,
        min_price: filters.minPrice,
        max_price: filters.maxPrice,
        date_from: filters.dateFrom,
        date_to: filters.dateTo,
        sort_by: sortBy,
        sort_order: sortOrder,
    });

    const moveMutation = useMoveToPipeline();
    const deleteMutation = useDeleteImportedItem();
    const bulkDeleteMutation = useBulkDeleteImportedItems();
    const bulkMoveMutation = useBulkMoveToPursuits();

    const handleDelete = useMemo(() => (item: any) => {
        setConfirmConfig({
            title: 'Delete Imported Bid',
            message: 'Are you sure you want to delete this imported bid? This action cannot be undone.',
            confirmText: 'Delete',
            confirmColor: 'red',
            isLoading: deleteMutation.isPending,
            onConfirm: async () => {
                try {
                    await deleteMutation.mutateAsync(item.id);
                    toast({ title: 'Item deleted', status: 'success' });
                    setSelectedIds(prev => prev.filter(id => id !== item.id));
                    onConfirmClose();
                    refetch();
                } catch (e) {
                    toast({ title: 'Failed to delete', status: 'error' });
                }
            }
        });
        onConfirmOpen();
    }, [deleteMutation, onConfirmOpen, onConfirmClose, refetch, toast]);

    const handleBulkDelete = () => {
        setConfirmConfig({
            title: 'Bulk Delete Bids',
            message: `Are you sure you want to delete ${selectedIds.length} items? This action cannot be undone.`,
            confirmText: 'Delete All',
            confirmColor: 'red',
            isLoading: bulkDeleteMutation.isPending,
            onConfirm: async () => {
                try {
                    await bulkDeleteMutation.mutateAsync(selectedIds);
                    toast({ title: `${selectedIds.length} items deleted`, status: 'success' });
                    setSelectedIds([]);
                    onConfirmClose();
                    refetch();
                } catch (e) {
                    toast({ title: 'Failed to delete items', status: 'error' });
                }
            }
        });
        onConfirmOpen();
    };

    const handleBulkMove = () => {
        setConfirmConfig({
            title: 'Move Selected to Pursuits',
            message: `Move ${selectedIds.length} items to Pursuits? This will organize them as cards on the Pursuits page.`,
            confirmText: 'Move to Pursuits',
            confirmColor: 'brand',
            isLoading: bulkMoveMutation.isPending,
            onConfirm: async () => {
                try {
                    await bulkMoveMutation.mutateAsync(selectedIds);
                    toast({ title: `${selectedIds.length} items moved to Pursuits`, status: 'success' });
                    setSelectedIds([]);
                    onConfirmClose();
                    refetch();
                } catch (e) {
                    toast({ title: 'Failed to move items', status: 'error' });
                }
            }
        });
        onConfirmOpen();
    };

    const handleMove = async (item: any) => {
        try {
            await moveMutation.mutateAsync({ id: item.id });
            toast({ title: 'Moved to Pursuits!', status: 'success' });
            onDetailsClose();
            refetch(); // Refresh list to remove moved item
        } catch (e) {
            toast({ title: 'Failed to move', status: 'error' });
        }
    };

    const handleRowClick = useMemo(() => (item: any) => {
        setSelectedItem(item);
        onDetailsOpen();
    }, [onDetailsOpen]);

    // Keep selected item in sync with fresh data from list (e.g. after AI summary generation)
    useEffect(() => {
        if (selectedItem && data?.items) {
            const freshItem = data.items.find((i: any) => i.id === selectedItem.id);
            if (freshItem && freshItem !== selectedItem) {
                setSelectedItem(freshItem);
            }
        }
    }, [data?.items, selectedItem]);

    const handleFilterChange = (newFilters: FilterState) => {
        setFilters(newFilters);
        setPage(1); // Reset to page 1 on filter change
    };

    // Count active filters
    const activeFilterCount =
        filters.agencyIds.length +
        (filters.minPrice ? 1 : 0) +
        (filters.maxPrice ? 1 : 0) +
        (filters.dateFrom ? 1 : 0) +
        (filters.dateTo ? 1 : 0);

    // Table Columns configuration
    const columnHelper = createColumnHelper<any>();
    const columns = useMemo(() => [
        columnHelper.accessor('description', {
            header: 'Description',
            cell: info => (
                <Tooltip label={info.getValue()} hasArrow placement="top-start" openDelay={500}>
                    <Text noOfLines={1} fontSize="sm" maxW="400px">
                        {info.getValue()}
                    </Text>
                </Tooltip>
            ),
        }),
        columnHelper.accessor('agency', {
            header: 'Agency',
            cell: info => <Badge variant="subtle">{info.getValue() || info.row.original.feed_name}</Badge>,
        }),
        columnHelper.accessor('project_title', {
            header: 'Department',
            cell: info => <Text noOfLines={1} title={info.getValue()} fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>{info.getValue() || '-'}</Text>,
        }),

        columnHelper.accessor('pre_bid_meeting', {
            header: 'Pre-Bid Date',
            cell: info => {
                const date = info.getValue();
                return <Text fontSize="xs" color="gray.600" _dark={{ color: "gray.400" }}>
                    {date ? new Date(date).toLocaleDateString() : '-'}
                </Text>;
            },
        }),
        columnHelper.accessor(
            type === 'historical' ? 'award_date' : 'submission_deadline',
            {
                header: type === 'historical' ? 'Award Date' : 'Deadline',
                cell: info => {
                    const val = info.getValue();
                    return val ? new Date(val).toLocaleDateString() : '-';
                },
            }
        ),
        columnHelper.display({
            id: 'docs',
            header: 'Docs',
            cell: info => {
                const { document_status, documents_url } = info.row.original;
                if (document_status === 'ready' && documents_url) {
                    return (
                        <Icon
                            as={MdDescription}
                            color="brand.500"
                            boxSize={5}
                            cursor="pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                const baseUrl = import.meta.env.VITE_API_URL || '/api';
                                handlePreview(`${baseUrl}/v1/documents/bid/${info.row.original.id}/pdf`);
                            }}
                        />
                    );
                }
                if (document_status === 'fetching') {
                    return <Spinner size="sm" color="brand.500" />;
                }
                if (document_status === 'failed') {
                    return <Icon as={MdError} color="red.400" boxSize={5} />;
                }
                return <Icon as={MdOutlineDescription} color="gray.400" boxSize={5} />;
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            cell: info => (
                <HStack spacing={1}>
                    <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="brand"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(info.row.original);
                        }}
                    >
                        View
                    </Button>
                    <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(info.row.original);
                        }}
                        isLoading={deleteMutation.isPending && deleteMutation.variables === info.row.original.id}
                    >
                        <Icon as={MdDelete} />
                    </Button>
                </HStack>
            ),
        }),
    ], [type, deleteMutation.isPending, deleteMutation.variables, columnHelper, handleDelete, handlePreview, handleRowClick]);

    const items = data?.items || [];
    const total = data?.total || 0;
    const totalPages = data?.pages || 0;

    return (
        <Flex h="calc(100vh - 200px)" overflow="hidden" direction="column">
            {/* Toolbar */}
            <Flex
                mb={4}
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                bg="white"
                _dark={{ bg: 'gray.800' }}
                p={3}
                borderRadius="md"
                shadow="sm"
                gap={3}
            >
                <Flex direction={{ base: 'column', sm: 'row' }} gap={3} flex={1}>
                    <InputGroup maxW={{ base: 'full', sm: '300px', md: '400px' }}>
                        <InputLeftElement pointerEvents="none">
                            <Icon as={MdSearch} color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Search bids..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(1);
                            }}
                            bg="white"
                            _dark={{ bg: 'gray.700' }}
                            size="sm"
                            borderRadius="md"
                        />
                    </InputGroup>

                    <HStack spacing={2}>
                        <Button
                            leftIcon={<MdFilterList />}
                            onClick={onFiltersOpen}
                            size="sm"
                            colorScheme={activeFilterCount > 0 ? 'brand' : 'gray'}
                            variant={activeFilterCount > 0 ? 'solid' : 'outline'}
                        >
                            Filters
                            {activeFilterCount > 0 && (
                                <Badge ml={2} borderRadius="full" colorScheme="white" variant="solid" color="brand.600">
                                    {activeFilterCount}
                                </Badge>
                            )}
                        </Button>

                        {activeFilterCount > 0 && (
                            <Button size="xs" variant="ghost" color="gray.500" onClick={() => setFilters({
                                agencyIds: [],
                                minPrice: undefined,
                                maxPrice: undefined,
                                dateFrom: undefined,
                                dateTo: undefined,
                            })}>
                                Clear All
                            </Button>
                        )}
                    </HStack>
                </Flex>

                <Flex align="center" gap={3} justify="space-between">
                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                        {total} {total === 1 ? 'result' : 'results'}
                    </Text>

                    {isScraping && (
                        <Badge colorScheme="brand" variant="solid" display="flex" alignItems="center" gap={1}>
                            <Spinner size="xs" /> Scraping...
                        </Badge>
                    )}
                </Flex>

                {selectedIds.length > 0 && (
                    <Flex
                        direction={{ base: 'column', sm: 'row' }}
                        gap={2}
                        p={{ base: 2, sm: 0 }}
                        bg={{ base: 'gray.50', sm: 'transparent' }}
                        _dark={{ base: 'gray.700', sm: 'gray.800' }}
                        borderRadius="md"
                        borderLeftWidth={{ base: 0, sm: '1px' }}
                        pl={{ base: 2, sm: 4 }}
                        align={{ base: 'stretch', sm: 'center' }}
                    >
                        <Text fontSize="xs" fontWeight="bold" color="brand.500">{selectedIds.length} selected</Text>
                        <HStack spacing={2}>
                            <Button
                                size="xs"
                                colorScheme="brand"
                                onClick={handleBulkMove}
                                isLoading={bulkMoveMutation.isPending}
                                flex={1}
                            >
                                Move to Pursuits
                            </Button>
                            <Button
                                size="xs"
                                colorScheme="red"
                                variant="ghost"
                                leftIcon={<MdDelete />}
                                onClick={handleBulkDelete}
                                isLoading={bulkDeleteMutation.isPending}
                            >
                                Delete
                            </Button>
                        </HStack>
                    </Flex>
                )}
            </Flex>

            {/* Main Table Area */}
            <Box flex="1" overflowY="auto" borderRadius="lg" borderWidth="1px" bg="white" _dark={{ bg: 'gray.800' }}>
                {isLoading ? (
                    <Center py={20}><Spinner size="xl" /></Center>
                ) : items.length === 0 ? (
                    <Center py={20} flexDirection="column">
                        <Text fontSize="lg" color="gray.500">No items found matching your filters.</Text>
                        <Button mt={4} size="sm" onClick={() => setFilters({
                            agencyIds: [],
                            minPrice: undefined,
                            maxPrice: undefined,
                            dateFrom: undefined,
                            dateTo: undefined,
                        })}>Clear Filters</Button>
                    </Center>
                ) : (
                    <DataTable
                        data={items}
                        columns={columns}
                        total={total}
                        page={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onSortChange={(id, order) => {
                            setSortBy(id);
                            setSortOrder(order);
                        }}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        enableRowSelection
                        onRowSelectionChange={setSelectedIds}
                    />
                )}
            </Box>

            {/* Filter Drawer */}
            <Drawer
                isOpen={isFiltersOpen}
                placement='left'
                onClose={onFiltersClose}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth='1px'>Filter Bids</DrawerHeader>

                    <DrawerBody>
                        <ImportedItemsFilters
                            filters={filters}
                            onChange={handleFilterChange}
                            onReset={() => setFilters({
                                agencyIds: [],
                                minPrice: undefined,
                                maxPrice: undefined,
                                dateFrom: undefined,
                                dateTo: undefined,
                            })}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Item Details Modal */}
            <ItemDetailsModal
                isOpen={isDetailsOpen}
                onClose={onDetailsClose}
                item={selectedItem}
                onMove={handleMove}
                isMoving={moveMutation.isPending}
                onRefresh={refetch}
            />

            {/* PDF Preview Modal */}
            <PDFPreviewModal
                isOpen={isPreviewOpen}
                onClose={onPreviewClose}
                pdfUrl={previewUrl}
            />

            {/* Confirmation Dialog */}
            {confirmConfig && (
                <ConfirmDialog
                    isOpen={isConfirmOpen}
                    onClose={onConfirmClose}
                    onConfirm={confirmConfig.onConfirm}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    confirmText={confirmConfig.confirmText}
                    confirmColor={confirmConfig.confirmColor}
                    isLoading={confirmConfig.isLoading}
                />
            )}
        </Flex>
    );
}

export function ImportedItems() {
    return (
        <Box h="100%">
            <Heading size="lg" mb={6}>Imported Bids</Heading>
            <Card h="full" bg="transparent" shadow="none">
                <CardBody p={0}>
                    <Tabs colorScheme="brand" isLazy h="full" display="flex" flexDirection="column" size={{ base: 'sm', md: 'md' }}>
                        <TabList
                            px={0}
                            pt={0}
                            borderBottom="none"
                            mb={4}
                            overflowX="auto"
                            overflowY="hidden"
                            sx={{
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': { display: 'none' },
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <Tab _selected={{ color: 'brand.500', fontWeight: 'bold', borderBottomColor: 'brand.500' }}>Active Opportunities</Tab>
                            <Tab _selected={{ color: 'brand.500', fontWeight: 'bold', borderBottomColor: 'brand.500' }}>Historical Awards</Tab>
                            <Tab _selected={{ color: 'brand.500', fontWeight: 'bold', borderBottomColor: 'brand.500' }}>Future Buying Plans</Tab>
                        </TabList>
                        <TabPanels flex="1" h="full">
                            <TabPanel p={0} h="full"><TabContent type="active" /></TabPanel>
                            <TabPanel p={0} h="full"><TabContent type="historical" /></TabPanel>
                            <TabPanel p={0} h="full"><TabContent type="future" /></TabPanel>
                        </TabPanels>
                    </Tabs>
                </CardBody>
            </Card>
        </Box>
    );
}
